import { lazy, Suspense, useEffect } from "react";
import { captureUtm } from "@/components/WhatsAppButton";
import { HelmetProvider } from "react-helmet-async";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Landing from "./pages/Landing";
import { CookieBanner } from "@/components/CookieBanner";

// Lazy load non-critical pages for code splitting
const Promo = lazy(() => import("./pages/Promo"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const BotPrivacyPolicy = lazy(() => import("./pages/BotPrivacyPolicy"));
const BotTermsOfService = lazy(() => import("./pages/BotTermsOfService"));
const Index = lazy(() => import("./pages/Index"));
const Thanks = lazy(() => import("./pages/Thanks"));
const PromoTr = lazy(() => import("./pages/PromoTr"));
const Agents = lazy(() => import("./pages/Agents"));
const PresentationForAgents = lazy(() => import("./pages/PresentationForAgents"));
const DemoVideo = lazy(() => import("./pages/DemoVideo"));
const Lanes = lazy(() => import("./pages/Lanes"));


import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";

const queryClient = new QueryClient();

// Hard redirect to a static page in /public (bypasses the SPA router)
const StaticRedirect = ({ to }: { to: string }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
};


const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => {
  useEffect(() => {
    captureUtm();
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <SonnerToaster />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/bot/privacy" element={<BotPrivacyPolicy />} />
              <Route path="/bot/terms" element={<BotTermsOfService />} />
              <Route path="/promo" element={<Promo />} />
              <Route path="/legacy" element={<Index />} />
              <Route path="/promo_tr" element={<PromoTr />} />
              <Route path="/thanks" element={<Thanks />} />
              <Route path="/agents" element={<Agents />} />
              <Route path="/presentation_for_agents" element={<PresentationForAgents />} />
              <Route path="/demo-video" element={<DemoVideo />} />
              <Route path="/lanes" element={<Lanes />} />
              <Route path="/landing" element={<Navigate to="/" replace />} />
              <Route path="/promo_tr1" element={<StaticRedirect to="/promo_tr1/" />} />
              <Route path="/promo_tr2" element={<StaticRedirect to="/promo_tr2/" />} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieBanner />
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
  );
};

export default App;
